import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ProductFilter from '../../components/ui/ProductFilter';
import ProductSearch from './components/ProductSearch';
import CategoryFilter from './components/CategoryFilter';
import ProductGrid from './components/ProductGrid';
import ProductCard from './components/ProductCard';
import Icon from '../../components/AppIcon';
import { getJson } from '../../api/client';
import { getProductImageUrl } from '../../utils/productImage';
import ProductGallery from '../product-detail/components/ProductGallery';
import ProductInfo from '../product-detail/components/ProductInfo';
import ProductTabs from '../product-detail/components/ProductTabs';
import QuoteModal from '../product-detail/components/QuoteModal';

const ProductPortfolio = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('Endoscopy');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Helper for hierarchical filtering
  const getSubtreeCategoryNames = (categoryId, categoryList) => {
    const names = [categoryId];
    const category = categoryList.find(c => c.id === categoryId);
    if (category && category.children) {
      category.children.forEach(child => {
        names.push(...getSubtreeCategoryNames(child.id, categoryList));
      });
    }
    return names;
  };

  // Product catalog is loaded from GET /products (no local mock list).

  const iconMap = {
    ERCP: 'Eye',
    Gastrostomy: 'Heart',
    Hemostasis: 'Droplets',
    'SEMS Stenting': 'Zap',
    'Endoscopy Accessories': 'Settings',
    'Laparoscopy Accessories': 'Settings',
    Polypectomy: 'Scissors',
    'Rapid Urease Test': 'TestTube',
    'Obesity Management': 'Activity',
    'Endo-Urology': 'Wind',
    'Access Products': 'Zap',
    'Stricture Management / Dilation Products': 'ArrowUpDown',
    'Drainage Products': 'Droplets',
    'Stone Management Products': 'Circle',
    'Turp / Accessories': 'Scissors',
    'Laparoscopy Products': 'Eye',
    'Access': 'Zap',
    'FlexDex Needle Driver': 'Target',
    'Wound Drainage': 'Heart',
    'Close Wound Drainage System Kit': 'FileBox',
    'Therapeutic': 'Activity',
    'Diagnostic': 'Search',
    'Cleaning Brush': 'Brush',
    'Disposable Rat Tooth Alligator': 'Scissors',
    'Disposable Bite Block': 'Shield',
    'Disposable Biopsy Forceps': 'Scissors',
    'Adjustable Intragastric Balloon': 'Activity',
  };

  const processHierarchyWithIcons = (nodes) => {
    return nodes.map(node => ({
      ...node,
      icon: iconMap[node.name] || node.icon || 'Folder',
      children: node.children ? processHierarchyWithIcons(node.children) : []
    }));
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      try {
        const [types, list] = await Promise.all([
          getJson('/product-types'),
          getJson('/products'),
        ]);

        if (!isMounted) return;

        setProductTypes(Array.isArray(types) ? types : []);
        setProducts(Array.isArray(list) ? list : []);
        setFetchError(null);
      } catch (error) {
        console.error('Failed to fetch products/product types.', error);
        if (!isMounted) return;
        setFetchError(error?.message ?? String(error));
        setProductTypes([]);
        setProducts([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // No mock fallback: use only API products; when API errors, list is empty
  const effectiveProducts = useMemo(
    () => products ?? [],
    [products]
  );
  
  const derivedCategories = useMemo(() => {
    const counts = (effectiveProducts ?? []).reduce((acc, p) => {
      const key = p?.category != null ? String(p.category).trim() : null;
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const descriptionMap = {
      ERCP: 'ERCP procedures and accessories',
      Gastrostomy: 'PEG kits and replacement tubes',
      Hemostasis: 'Bleeding control devices',
      'SEMS Stenting': 'Self-expanding metallic stents',
      Accessories: 'Endoscopic accessories',
      Polypectomy: 'Polyp removal devices',
      'Rapid Urease Test': 'H. pylori detection tests',
      'Obesity Management': 'Weight management solutions',
      'Endo-Urology Access': 'Urological access devices',
      'Endo-Urology Dilation': 'Urological dilation instruments',
      'Endo-Urology Drainage': 'Drainage catheters and stents',
      'Endo-Urology Stone Management': 'Stone retrieval and management',
      'Endo-Urology TURP': 'TURP instruments and accessories',
      'Laparoscopy Access': 'Laparoscopic access devices',
      'Laparoscopy Accessories': 'Laparoscopic accessories',
      'Wound Drainage': 'Wound drainage systems',
    };

    // Build tree from flat product-types list with aggregated counts
    const buildTree = (items, parentId = null) => {
      return items
        .filter((item) => item.parent_id === parentId)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((item) => {
          const children = buildTree(items, item.product_type_id);
          const childrenCount = children.reduce((sum, child) => sum + (child.count || 0), 0);
          
          return {
            id: item.name,
            name: item.name,
            slug: item.slug,
            icon: iconMap[item.name] || 'Package',
            count: (counts[item.name] ?? 0) + childrenCount,
            description: descriptionMap[item.name],
            children: children,
          };
        });
    };

    const tree = buildTree(productTypes ?? []);
    
    // Add "All Products" at the top
    const totalCount = (effectiveProducts ?? []).length;
    
    return [
      /* {
        id: 'all',
        name: 'All Products',
        icon: 'Package',
        count: totalCount,
        color: 'text-text-primary',
        bgColor: 'bg-muted',
        children: [],
      }, */
      ...tree,
    ];
  }, [effectiveProducts, productTypes]);

  // Keep selected category valid: reset to 'all' if current selection not in tree (flat check)
  useEffect(() => {
    if (selectedCategory === 'all') return;
    
    const flatten = (items) => {
      let result = [];
      items.forEach(item => {
        result.push(item);
        if (item.children) result = [...result, ...flatten(item.children)];
      });
      return result;
    };
    
    const flatCategories = flatten(derivedCategories);
    const ids = flatCategories.map(c => c.id).filter(Boolean);
    
    if (ids.length > 0 && !ids.includes(selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [derivedCategories, selectedCategory]);

  // Filter products based on category, main category, search query, and other filters


  // Filter products based on category, search query, and other filters
  useEffect(() => {
    let filtered = [...effectiveProducts];

    // Filter by category (including children)
    if (selectedCategory !== 'all' && selectedCategory) {
      const flatten = (items) => {
        let result = [];
        items.forEach(item => {
          result.push(item);
          if (item.children) result = [...result, ...flatten(item.children)];
        });
        return result;
      };
      
      const flatCats = flatten(derivedCategories);
      const categoryNamesToInclude = getSubtreeCategoryNames(selectedCategory, flatCats);
      
      const norm = (s) => (s != null ? String(s).trim() : '');
      filtered = filtered?.filter(
        (product) => categoryNamesToInclude.some(name => norm(product?.category) === norm(name))
      ) ?? [];
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.manufacturer?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Sort products
    switch (sortBy) {
      case 'name':
        filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'category':
        filtered?.sort((a, b) => a?.category?.localeCompare(b?.category));
        break;
      case 'newest':
        filtered?.sort((a, b) => b?.id - a?.id);
        break;
      case 'price-low':
        filtered?.sort((a, b) => {
          const pa = Number(a?.price);
          const pb = Number(b?.price);
          const va = Number.isFinite(pa) && pa > 0 ? pa : Infinity;
          const vb = Number.isFinite(pb) && pb > 0 ? pb : Infinity;
          return va - vb;
        });
        break;
      case 'price-high':
        filtered?.sort((a, b) => {
          const pa = Number(a?.price);
          const pb = Number(b?.price);
          const va = Number.isFinite(pa) && pa > 0 ? pa : -Infinity;
          const vb = Number.isFinite(pb) && pb > 0 ? pb : -Infinity;
          return vb - va;
        });
        break;
      default:
        // Keep original order for relevance
        break;
    }

    setFilteredProducts(filtered);
  }, [effectiveProducts, selectedCategory, searchQuery, sortBy]);

  const categoryPath = useMemo(() => {
    if (selectedCategory === 'all') return [{ id: 'all', name: 'All Products' }];
    
    const findPath = (items, targetId, path = []) => {
      for (const item of items) {
        if (item.id === targetId) return [...path, { id: item.id, name: item.name }];
        if (item.children) {
          const childPath = findPath(item.children, targetId, [...path, { id: item.id, name: item.name }]);
          if (childPath) return childPath;
        }
      }
      return null;
    };
    
    return findPath(derivedCategories, selectedCategory) || [{ id: selectedCategory, name: selectedCategory }];
  }, [selectedCategory, derivedCategories]);

  // For backward compatibility or single name usage
  const selectedCategoryName = categoryPath[categoryPath.length - 1]?.name;

  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
  };

  const breadcrumbItems = useMemo(() => {
    const items = [
      { label: 'Home', path: '/' },
      { label: 'Products', path: '/product-portfolio', onClick: handleBackToList }
    ];

    if (selectedCategory && selectedCategory !== 'all') {
      items.push({ 
        label: selectedCategory,
        onClick: handleBackToList
      });
    }

    if (selectedProduct) {
      items.push({ label: selectedProduct.name });
    }

    return items;
  }, [selectedCategory, selectedProduct]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query) setSelectedProduct(null);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedProduct(null);
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-500">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-8 py-6 sm:py-10">
          {/* Breadcrumb */}
          <Breadcrumb customItems={breadcrumbItems} />

          {/* Page Header */}
          {!selectedProduct && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex flex-col lg:flex-row lg:items-center py-4 gap-8 lg:gap-12 xl:gap-32">
                {/* Left Title Area */}
                <div className="flex-shrink-0">
                  <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-2 text-center lg:text-left">
                    Product Portfolio
                  </h1>
                  <p className="text-text-secondary text-lg text-center lg:text-left">
                    Explore our comprehensive range of medical devices
                  </p>
                </div>
                
                {/* Proximity-positioned Search Bar */}
                <div className="w-full lg:w-[500px] xl:w-[650px]">
                  <ProductSearch
                    onSearch={handleSearch}
                    onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                    products={effectiveProducts || []}
                    className="w-full"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* API error: show when fetch failed (list still shows from mock) */}
          {fetchError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive"
            >
              <p className="font-medium">API error</p>
              <p className="text-sm mt-1">{fetchError}</p>
            </motion.div>
          )}

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Proportional Desktop Filters */}
            <aside className={`hidden lg:block flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-hide pr-4 transition-all duration-500 ease-in-out ${
              isSidebarCollapsed ? 'lg:w-24 xl:w-24 2xl:w-28' : 'lg:w-72 xl:w-80 2xl:w-96'
            }`}>
              <CategoryFilter
                selectedCategory={selectedCategory}
                categories={derivedCategories}
                onCategoryChange={handleCategoryChange}
                totalProducts={effectiveProducts?.length ?? 0}
                categoryCount={Math.max(0, (derivedCategories?.length ?? 1) - 1)}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              />
            </aside>

            {/* Mobile Filter Floating Button */}
            {!selectedProduct && (
              <div className="lg:hidden fixed bottom-8 left-1/2 -translate-x-1/2 z-40">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsFilterOpen(true)}
                  className="flex items-center space-x-3 px-8 py-4 bg-primary text-primary-foreground rounded-full shadow-xl shadow-primary/30 font-bold border-2 border-primary-foreground/10"
                >
                  <Icon name="Layers" size={20} />
                  <span>Browse Categories</span>
                  <span className="bg-primary-foreground/20 px-2 py-0.5 rounded-lg text-xs ml-2">
                    {derivedCategories.length - 1}
                  </span>
                </motion.button>
              </div>
            )}

            {/* Mobile Filter Bottom Sheet */}
            <AnimatePresence>
              {isFilterOpen && (
                <div className="lg:hidden fixed inset-0 z-50">
                  {/* Backdrop */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setIsFilterOpen(false)}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                  />
                  
                  {/* Bottom Sheet */}
                  <motion.aside
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="absolute bottom-0 left-0 right-0 bg-background rounded-t-[2.5rem] p-8 max-h-[85vh] overflow-y-auto shadow-2xl border-t border-border"
                  >
                    <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mb-8 opacity-50" />
                    
                    <div className="flex items-center justify-between mb-8">
                      <div>
                        <h2 className="text-2xl font-black text-text-primary">Product Categories</h2>
                        <p className="text-sm text-text-secondary mt-1">Explore our medical solutions</p>
                      </div>
                      <button 
                        onClick={() => setIsFilterOpen(false)} 
                        className="w-12 h-12 flex items-center justify-center bg-muted rounded-2xl hover:bg-muted/80 transition-colors"
                      >
                        <Icon name="X" size={24} className="text-text-secondary" />
                      </button>
                    </div>

                    <CategoryFilter
                      selectedCategory={selectedCategory}
                      categories={derivedCategories}
                      onCategoryChange={(cat) => {
                        handleCategoryChange(cat);
                        setIsFilterOpen(false);
                      }}
                      totalProducts={effectiveProducts?.length ?? 0}
                      categoryCount={Math.max(0, (derivedCategories?.length ?? 1) - 1)}
                    />
                    
                    <div className="h-10" /> {/* Extra spacing for bottom */}
                  </motion.aside>
                </div>
              )}
            </AnimatePresence>

            {/* Main Content Area */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex-1 min-w-0"
            >
              {selectedProduct ? (
                <ProductDetailInline
                  product={selectedProduct}
                  onBack={handleBackToList}
                  onProductSelect={handleProductSelect}
                />
              ) : (
                <ProductGrid
                  products={filteredProducts}
                  categoryPath={categoryPath}
                  onCategoryChange={handleCategoryChange}
                  viewMode={viewMode}
                  loading={loading}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onViewModeChange={toggleViewMode}
                  onResetFilters={() => { setSearchQuery(''); setSelectedCategory('Endoscopy'); }}
                  onProductSelect={handleProductSelect}
                />
              )}
            </motion.div>
          </div>

          {/* Bottom CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 border border-primary/10"
          >
            <div className="text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageCircle" size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                Need Help Finding the Right Product?
              </h3>
              <p className="text-text-secondary mb-6">
                Our medical device specialists are here to help you find the perfect solution for your clinical needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
                >
                  <Icon name="Phone" size={16} className="mr-2" />
                  Contact Our Experts
                </button>
                <button
                  onClick={() => navigate('/about-us')}
                  className="inline-flex items-center justify-center px-6 py-3 border border-border text-text-primary rounded-lg hover:bg-muted transition-colors duration-200"
                >
                  <Icon name="Info" size={16} className="mr-2" />
                  Learn More About Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };
  
  // Inline Product Detail View Component
  const ProductDetailInline = ({ product, onBack, onProductSelect }) => {
  const [fullProduct, setFullProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    if (!product?.id) return;
    
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const idOrSlug = product.slug || product.id;
        const [detailData, relatedData] = await Promise.all([
          getJson(`/products/${encodeURIComponent(idOrSlug)}`),
          getJson(`/products/${encodeURIComponent(idOrSlug)}/related`)
        ]);

        setFullProduct(detailData);
        setRelatedProducts(Array.isArray(relatedData) ? relatedData : []);
      } catch (err) {
        console.error('Failed to fetch product data', err);
        setFullProduct(product);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [product?.id]);

  const handleRequestQuote = (productForQuote, quantity) => {
    setSelectedQuantity(quantity);
    setIsQuoteModalOpen(true);
  };

  const displayProduct = fullProduct || product;

  if (loading && !fullProduct) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-6">
        <div className="w-14 h-14 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-text-secondary font-bold tracking-wide uppercase text-xs">Loading Catalog Item...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl border border-border overflow-hidden"
      >
        <div className="p-4 md:p-6 border-b border-border bg-muted/30">
          <button 
            onClick={onBack}
            className="flex items-center text-text-secondary hover:text-primary font-bold transition-colors text-sm"
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Return to Listing
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Product Gallery Section */}
            <div className="lg:col-span-6 space-y-6">
              <ProductGallery product={displayProduct} />
              
              {/* Key Features Section - Hot coded */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center">
                  <Icon name="Star" size={20} className="mr-2 text-primary fill-primary/20" />
                  Key Clinical Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Ergonomic control for precise manipulation',
                    'Advanced radiopaque markings for visibility',
                    'Smooth-pass technology for easy insertion',
                    'High tensile strength biocompatible materials'
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} className="text-primary" />
                      </div>
                      <p className="text-sm text-text-secondary font-medium leading-tight">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Product Info Section */}
            <div className="lg:col-span-6">
              <ProductInfo 
                product={displayProduct} 
                onRequestQuote={handleRequestQuote} 
              />
            </div>
          </div>

          <div className="mt-12">
            <ProductTabs 
              product={displayProduct} 
              relatedProducts={relatedProducts} 
              onSelect={onProductSelect}
            />
          </div>
        </div>
      </motion.div>

      {/* Related Products Grid (Our Portfolio Style) */}
      {/* {relatedProducts.length > 0 && (
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-text-primary uppercase tracking-tight">Related Equipment</h2>
            <div className="h-px flex-1 bg-border mx-6 hidden md:block opacity-50" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {relatedProducts.map((relProduct) => (
              <ProductCard 
                key={relProduct.id} 
                product={relProduct} 
                onSelect={onProductSelect}
              />
            ))}
          </div>
        </section>
      )} */}

      {/* Trust Badges - From Detail Page style */}
      <div className="bg-muted/30 border border-border rounded-2xl p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: 'Shield', color: 'success', title: 'Certified Safety', desc: 'Full GDPMD compliance' },
          { icon: 'Truck', color: 'primary', title: 'Direct Dispatch', desc: 'Secure medical logistics' },
          { icon: 'Headphones', color: 'warning', title: 'Technical Support', desc: 'Expert clinical training' }
        ].map((Badge, idx) => (
          <div key={idx} className="flex items-center gap-4">
            <div className={`w-12 h-12 bg-${Badge.color}/10 rounded-xl flex items-center justify-center`}>
              <Icon name={Badge.icon} size={24} className={`text-${Badge.color}`} />
            </div>
            <div>
              <h4 className="font-bold text-text-primary">{Badge.title}</h4>
              <p className="text-xs text-text-secondary">{Badge.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quote Modal Overlay */}
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        product={product}
        quantity={selectedQuantity}
      />
    </div>
  );
};

export default ProductPortfolio;
